import React, { useState } from "react";
import {
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  MapPin,
  ExternalLink,
  Phone,
  Copy,
  Reply,
  ChevronLeft,
  MoreVertical,
} from "lucide-react";

import whatsappBg from "src/assets/whatsapp-bg.png";
import { splitVariables, isVariableToken, fillVariables } from "../templateUtils";

/* ---------- shared bits ---------- */

function RichText({ text, samples, fill }) {
  if (fill) {
    return <>{fillVariables(text, samples)}</>;
  }
  return (
    <>
      {splitVariables(text).map((part, i) =>
        isVariableToken(part) ? (
          <span
            key={i}
            className="rounded bg-[#25d366]/15 px-1 text-[11px] font-medium text-[#075e54]"
          >
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

const MediaBlock = ({ format, preview }) => {
  const map = {
    IMAGE: { Icon: ImageIcon, label: "Image" },
    VIDEO: { Icon: VideoIcon, label: "Video" },
    DOCUMENT: { Icon: FileText, label: "Document" },
    LOCATION: { Icon: MapPin, label: "Location" },
  };
  const { Icon, label } = map[format] || map.IMAGE;

  if (preview && format === "IMAGE") {
    return (
      <img
        src={preview}
        alt=""
        className="mb-1.5 h-32 w-full rounded-lg object-cover"
      />
    );
  }
  if (preview && format === "VIDEO") {
    return (
      <video src={preview} className="mb-1.5 h-32 w-full rounded-lg object-cover" />
    );
  }
  return (
    <div className="mb-1.5 flex h-24 items-center justify-center gap-2 rounded-lg bg-black/5 text-slate-400">
      <Icon size={20} />
      <span className="text-[11px] font-medium">{label}</span>
    </div>
  );
};

const BTN_ICON = {
  URL: ExternalLink,
  PHONE_NUMBER: Phone,
  COPY_CODE: Copy,
  QUICK_REPLY: Reply,
};

const ButtonList = ({ buttons }) => {
  if (!buttons?.length) return null;
  return (
    <div className="mt-1.5 space-y-px overflow-hidden rounded-b-lg border-t border-black/5">
      {buttons.map((b, i) => {
        const Icon = BTN_ICON[b.type] || Reply;
        return (
          <div
            key={i}
            className="flex items-center justify-center gap-1.5 bg-white py-2 text-[13px] font-medium text-[#00a5f4]"
          >
            <Icon size={14} />
            {b.text || (b.type === "COPY_CODE" ? "Copy code" : b.type)}
          </div>
        );
      })}
    </div>
  );
};

/* ---------- message bodies ---------- */

function StandardBubble({ form, fill }) {
  const { header, body, footer, buttons } = form;
  return (
    <div className="w-[85%] overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
      <div className="p-2">
        {header.format === "TEXT" && header.text && (
          <p className="mb-1 text-[13px] font-semibold text-slate-900">
            <RichText text={header.text} samples={[header.example]} fill={fill} />
          </p>
        )}
        {["IMAGE", "VIDEO", "DOCUMENT", "LOCATION"].includes(header.format) && (
          <MediaBlock format={header.format} preview={header.media_preview} />
        )}

        <p className="whitespace-pre-wrap text-[13px] leading-5 text-slate-800">
          {body.text ? (
            <RichText text={body.text} samples={body.examples} fill={fill} />
          ) : (
            <span className="text-slate-400">Your message will appear here…</span>
          )}
        </p>

        {footer.text && (
          <p className="mt-1.5 text-[11px] text-slate-400">{footer.text}</p>
        )}

        <div className="mt-1 text-right text-[10px] text-slate-400">
          {time()} ✓✓
        </div>
      </div>
      <ButtonList buttons={buttons} />
    </div>
  );
}

function AuthBubble({ auth, fill }) {
  const code = "972563";
  return (
    <div className="w-[85%] overflow-hidden rounded-lg rounded-tl-none bg-white shadow-sm">
      <div className="p-2">
        <p className="text-[13px] leading-5 text-slate-800">
          <strong>{fill ? code : "{{1}}"}</strong> is your verification code.
          {auth.add_security_recommendation &&
            " For your security, do not share this code."}
        </p>
        {auth.code_expiration_minutes ? (
          <p className="mt-1.5 text-[11px] text-slate-400">
            This code expires in {auth.code_expiration_minutes} minutes.
          </p>
        ) : null}
        <div className="mt-1 text-right text-[10px] text-slate-400">{time()} ✓✓</div>
      </div>
      <ButtonList
        buttons={[
          {
            type: auth.otp_type === "COPY_CODE" ? "COPY_CODE" : "QUICK_REPLY",
            text:
              auth.otp_button_text ||
              (auth.otp_type === "COPY_CODE" ? "Copy code" : "Autofill"),
          },
        ]}
      />
    </div>
  );
}

function CarouselBubbles({ form, fill }) {
  const { body, carousel } = form;
  return (
    <div className="w-full space-y-2">
      <div className="w-[85%] rounded-lg rounded-tl-none bg-white p-2 shadow-sm">
        <p className="whitespace-pre-wrap text-[13px] leading-5 text-slate-800">
          {body.text ? (
            <RichText text={body.text} samples={body.examples} fill={fill} />
          ) : (
            <span className="text-slate-400">Intro message…</span>
          )}
        </p>
        <div className="mt-1 text-right text-[10px] text-slate-400">{time()} ✓✓</div>
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
        {(carousel.cards.length ? carousel.cards : [null, null]).map((card, i) => (
          <div
            key={card?.id || i}
            className="w-[70%] flex-shrink-0 overflow-hidden rounded-lg bg-white shadow-sm"
          >
            <div className="p-1.5">
              <MediaBlock
                format={card?.header.format || "IMAGE"}
                preview={card?.header.media_preview}
              />
              <p className="text-[12px] leading-4 text-slate-800">
                {card?.body.text ? (
                  <RichText
                    text={card.body.text}
                    samples={card.body.examples}
                    fill={fill}
                  />
                ) : (
                  <span className="text-slate-400">Card {i + 1}</span>
                )}
              </p>
            </div>
            <ButtonList buttons={card?.buttons} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- frame ---------- */

const TemplatePreview = ({ form }) => {
  const [fill, setFill] = useState(false);
  const isAuth = form.category === "AUTHENTICATION";
  const isCarousel =
    form.category === "MARKETING" && form.template_type === "CAROUSEL";

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={() => setFill((v) => !v)}
        className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
      >
        <span
          className={`h-2 w-2 rounded-full ${fill ? "bg-emerald-500" : "bg-slate-300"}`}
        />
        {fill ? "Showing sample values" : "Showing variables"}
      </button>

      <div className="w-[300px] rounded-[2.2rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
        <div className="relative flex h-6 items-center justify-center rounded-t-[1.4rem] bg-slate-900">
          <div className="h-1.5 w-16 rounded-full bg-slate-700" />
        </div>

        <div className="overflow-hidden rounded-b-[1.4rem] bg-[#e5ddd5]">
          {/* chat header */}
          <div className="flex items-center gap-2 bg-[#075e54] px-3 py-2 text-white">
            <ChevronLeft size={16} />
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-[11px] font-semibold">
              B
            </div>
            <div className="flex-1 leading-tight">
              <p className="text-[12px] font-semibold">Business</p>
              <p className="text-[10px] text-white/70">business account</p>
            </div>
            <MoreVertical size={15} />
          </div>

          {/* messages */}
          <div
            className="min-h-[380px] space-y-2 px-2.5 py-3"
            style={{
              backgroundImage: `url(${whatsappBg})`,
              backgroundSize: "cover",
            }}
          >
            {isAuth ? (
              <AuthBubble auth={form.authentication} fill={fill} />
            ) : isCarousel ? (
              <CarouselBubbles form={form} fill={fill} />
            ) : (
              <StandardBubble form={form} fill={fill} />
            )}
          </div>
        </div>
      </div>

      <p className="mt-3 max-w-[280px] text-center text-[11px] text-slate-400">
        Live preview — the real message uses your account name and the recipient’s
        values.
      </p>
    </div>
  );
};

function time() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default TemplatePreview;
